import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'

export interface AccountIdentity {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  name: string
  greeting_name: string
  avatar_url?: string | null
  has_usable_password?: boolean
}

export interface UpdateAccountIdentityInput {
  name?: string
  greeting_name?: string
  first_name?: string
  last_name?: string
}

async function accountIdentityErrorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '')
  const trimmed = text.trim()
  if (!trimmed) return `Request failed (${response.status})`
  try {
    const data = JSON.parse(trimmed) as Record<string, unknown>
    if (typeof data.error === 'string' && data.error) return data.error
  } catch {
    /* not JSON */
  }
  return trimmed.length > 300 ? `${trimmed.slice(0, 297)}…` : trimmed
}

export function useAccountIdentity() {
  const { isAuthenticated, user, loginWithTokens, token } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['account-identity'],
    queryFn: async (): Promise<AccountIdentity> => {
      const response = await authorizedFetch('/api/accounts/me')
      if (!response.ok) {
        throw new Error(await accountIdentityErrorMessage(response))
      }
      return response.json()
    },
    enabled: isAuthenticated,
  })

  const syncIdentityCaches = (data: AccountIdentity) => {
    queryClient.setQueryData(['account-identity'], data)
    queryClient.setQueryData(['account-settings'], data)
    queryClient.invalidateQueries({ queryKey: ['profile'] })
  }

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateAccountIdentityInput): Promise<AccountIdentity> => {
      const response = await authorizedFetch('/api/accounts/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })
      if (!response.ok) {
        throw new Error(await accountIdentityErrorMessage(response))
      }
      return response.json()
    },
    onSuccess: (data) => {
      syncIdentityCaches(data)
      if (user && token) {
        loginWithTokens(token, {
          ...user,
          first_name: data.first_name,
          last_name: data.last_name,
          name: data.name,
        })
      }
    },
  })

  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File): Promise<AccountIdentity> => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await authorizedFetch('/api/accounts/me/avatar', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) {
        throw new Error(await accountIdentityErrorMessage(response))
      }
      return response.json()
    },
    onSuccess: (data) => {
      syncIdentityCaches(data)
    },
  })

  const deleteAvatarMutation = useMutation({
    mutationFn: async (): Promise<AccountIdentity> => {
      const response = await authorizedFetch('/api/accounts/me/avatar', {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error(await accountIdentityErrorMessage(response))
      }
      return response.json()
    },
    onSuccess: (data) => {
      syncIdentityCaches(data)
    },
  })

  return {
    identity: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    updateIdentity: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    uploadAvatar: uploadAvatarMutation.mutateAsync,
    isUploadingAvatar: uploadAvatarMutation.isPending,
    uploadAvatarError: uploadAvatarMutation.error,
    deleteAvatar: deleteAvatarMutation.mutateAsync,
    isDeletingAvatar: deleteAvatarMutation.isPending,
    deleteAvatarError: deleteAvatarMutation.error,
  }
}
